import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { ButtonBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles((theme) => ({
  input: {
    display: 'none',
  },
  image: {
    marginTop: theme.spacing(3),
    position: 'relative',
    height: 100,
    border: (props) => (props.error ? `2px solid ${theme.palette.error.main}` : 'none'),
    borderRadius: (props) => (props.type === 'circle' ? 50 : 0),
    [theme.breakpoints.down('xs')]: {
      width: '100% !important', // Overrides inline-style
      height: 100,
    },
    '&:hover, &$focusVisible': {
      zIndex: 1,
      '& $imageBackdrop': {
        opacity: 0.4,
      },
      '& $imageTitle': {
        opacity: 1,
      },
    },
  },
  focusVisible: {},
  imageSrc: {
    position: 'absolute',
    borderRadius: (props) => (props.type === 'circle' ? 50 : 0),
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageBackdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: (props) => (props.type === 'circle' ? 50 : 0),
    backgroundColor: theme.palette.common.black,
    opacity: 0.15,
    transition: theme.transitions.create('opacity'),
  },
  imageTitle: {
    color: 'white',
    display: 'inherit',
    fontSize: 12,
    maxWidth: 36,
    opacity: 0.0,
    position: 'relative',
    padding: `${theme.spacing(2)}px ${theme.spacing(4)}px ${theme.spacing(1) + 6}px`,
  },
  hidden: {
    display: 'none',
  },
}));

const MyInputImage = (props) => {
  const {
    name,
    value,
    onChange,
    inputRef,

    autoFocus,
    disabled,
    error,

    label,
    type,

    'data-testid': dataTestId,
  } = props;

  const defaultInputRef = React.useRef(null);
  const iRef = inputRef || defaultInputRef;
  const [width] = React.useState(100);
  const [height] = React.useState(100);
  const [preview, setPreview] = React.useState(value);
  const [fileName, setFileName] = React.useState('');
  const classes = useStyles({ error, type });

  const changePreview = (file) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        setPreview(e.target.result);
        setFileName(file.name);
      };
      reader.readAsDataURL(file);

      // if (type !== 'circle') {
      //   const url = window.URL || window.webkitURL;
      //   const img = new Image();
      //   img.onload = () => {};
      //   img.src = url.createObjectURL(file);
      // }
    }
  };

  return (
    <>
      <input
        data-testid={dataTestId}
        name={name}
        accept="image/*"
        className={classes.input}
        type="file"
        ref={iRef}
        onChange={(e) => {
          if (e.target.files.length > 0) {
            changePreview(e.target.files[0]);

            if (onChange) {
              onChange({
                target: {
                  name,
                  value: e.target.files[0],
                },
              });
            }
          }
        }}
      />
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label htmlFor={name}>
        <ButtonBase
          autoFocus={autoFocus}
          focusRipple
          className={classes.image}
          focusVisibleClassName={classes.focusVisible}
          onClick={() => {
            iRef.current.click();
          }}
          disabled={disabled}
          style={{ height, width }}
        >
          <img
            className={clsx(classes.imageSrc, {
              [classes.hidden]: !preview,
            })}
            src={
              preview
            }
            alt={preview ? `Uploaded file for ${name}${fileName ? ` is ${fileName}` : ''}` : ''}
          />
          <span className={classes.imageBackdrop} />
          <span className={classes.imageButton}>
            <Typography
              component="span"
              color="inherit"
              className={classes.imageTitle}
            >
              {label}
            </Typography>
          </span>
        </ButtonBase>
      </label>
      <div>
        <Typography variant="caption" color="textSecondary">
          {fileName}
        </Typography>
      </div>
    </>
  );
};

MyInputImage.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  onChange: PropTypes.func,
  inputRef: PropTypes.any,

  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,

  label: PropTypes.node,
  type: PropTypes.oneOf(['rectangle', 'circle']),

  'data-testid': PropTypes.string,
};

MyInputImage.defaultProps = {
  value: undefined,
  onChange: undefined,
  inputRef: undefined,

  autoFocus: false,
  disabled: false,
  error: false,

  label: <FormattedMessage id="myInputImage.label.changeImage" />,
  type: 'rectangle',

  'data-testid': undefined,
};

export default MyInputImage;
